import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { User, LogOut, Shield, Menu, LayoutDashboard, MessageSquare, Dumbbell } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Header = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user) return;
      try {
        const response = await axios.get(`${API_URL}/messages/unread-count`);
        setUnreadCount(response.data.unread_count || 0);
      } catch (error) {
        // Silently fail
      }
    };

    fetchUnreadCount();
    // Poll every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <span className="font-heading text-2xl font-bold tracking-tight text-white uppercase">
              TRAINR
            </span>
          </Link>

          {/* Navigation */}
          <nav className="flex items-center gap-4">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 text-white hover:text-primary"
                    data-testid="user-menu-button"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <span className="hidden sm:inline font-medium">{user.name}</span>
                    <Menu className="w-4 h-4 sm:hidden" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-[#141414] border-white/10">
                  <DropdownMenuItem className="text-gray-300 focus:text-white focus:bg-white/10">
                    <User className="mr-2 h-4 w-4" />
                    <span>{user.email}</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem
                    className="text-gray-300 focus:text-white focus:bg-white/10 cursor-pointer"
                    onClick={() => navigate('/trainer/dashboard')}
                    data-testid="trainer-dashboard-menu-item"
                  >
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    <span>Trainer Dashboard</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-gray-300 focus:text-white focus:bg-white/10 cursor-pointer"
                    onClick={() => navigate('/my-training')}
                    data-testid="my-training-menu-item"
                  >
                    <Dumbbell className="mr-2 h-4 w-4" />
                    <span>My Training</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-gray-300 focus:text-white focus:bg-white/10 cursor-pointer"
                    onClick={() => navigate('/messages')}
                    data-testid="messages-menu-item"
                  >
                    <MessageSquare className="mr-2 h-4 w-4" />
                    <span>Messages</span>
                    {unreadCount > 0 && (
                      <span className="ml-auto bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                        {unreadCount}
                      </span>
                    )}
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator className="bg-white/10" />
                      <DropdownMenuItem
                        className="text-primary focus:text-primary focus:bg-white/10 cursor-pointer"
                        onClick={() => navigate('/admin')}
                        data-testid="admin-menu-item"
                      >
                        <Shield className="mr-2 h-4 w-4" />
                        <span>Admin Dashboard</span>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem
                    className="text-red-400 focus:text-red-400 focus:bg-white/10 cursor-pointer"
                    onClick={handleLogout}
                    data-testid="logout-menu-item"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Logout</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button
                    variant="ghost"
                    className="text-gray-300 hover:text-white"
                    data-testid="login-button"
                  >
                    Login
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button
                    className="bg-primary text-white hover:bg-primary/90 glow-blue-hover transition-all"
                    data-testid="signup-button"
                  >
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
};
